import isEmpty from 'lodash/isEmpty';
import {
  HEATMAP_VISIBLE_FULL_ZOOM,
  HEATMAP_VISIBLE_START_ZOOM,
  HEATMAP_MAX_ZOOM,
  HEATMAP_MIN_ZOOM,
  HEATMAP_NORMALIZED_WEIGHT_PROPERTY,
} from './constants';

const NAMED_COLORS = {
  white: [255, 255, 255],
  blue: [0, 0, 255],
  black: [0, 0, 0],
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const parseColor = (color) => {
  if (!color || typeof color !== 'string') {
    return null;
  }

  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const normalizedHex = hex.length === 3
      ? hex.split('').map(char => `${char}${char}`).join('')
      : hex;

    if (normalizedHex.length !== 6) {
      return null;
    }

    return [
      parseInt(normalizedHex.slice(0, 2), 16),
      parseInt(normalizedHex.slice(2, 4), 16),
      parseInt(normalizedHex.slice(4, 6), 16),
    ];
  }

  const rgbaMatch = color.match(/^rgba?\(([^)]+)\)$/i);

  if (rgbaMatch) {
    return rgbaMatch[1]
      .split(',')
      .slice(0, 3)
      .map(part => parseInt(part.trim(), 10));
  }

  return NAMED_COLORS[color.toLowerCase()] || null;
};

const withAlpha = (color, alpha) => {
  const rgb = parseColor(color);

  if (!rgb) {
    return color;
  }

  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
};

const createHeatmapColorRamp = (gradientColors) => {
  if (isEmpty(gradientColors)) {
    return [];
  }

  const {
    lowestValue = 'rgba(255,255,255,0)',
    midpointValue,
    highestValue,
  } = gradientColors;
  const middleColor = midpointValue || highestValue;

  return [
    0.24, withAlpha(lowestValue, 0.06),
    0.42, withAlpha(lowestValue, 0.16),
    0.6, withAlpha(middleColor, 0.3),
    0.78, withAlpha(middleColor, 0.48),
    0.92, withAlpha(highestValue, 0.68),
    1, highestValue,
  ];
};

const getContrastProfile = (heatmapContrast = 50) => {
  const contrast = clamp(heatmapContrast, 0, 100);
  const spread = contrast / 100;

  return {
    radiusMultiplier: 0.78 + (spread * 0.7),
    intensityMultiplier: 1.02 - (spread * 0.32),
    opacityMultiplier: 1.02 - (spread * 0.12),
    rampShift: (0.5 - spread) * 0.22,
  };
};

const getVisibilityProfile = (heatmapVisibility = 50) => {
  const visibility = clamp(heatmapVisibility, 0, 100);
  const strength = visibility / 100;

  return {
    intensityMultiplier: 0.88 + (strength * 0.4),
    opacityMultiplier: 0.82 + (strength * 0.42),
    lowEndShift: (strength - 0.5) * 0.18,
  };
};

export const createGradient = (gradientColors, range) => {
  if (isEmpty(gradientColors)) {
    return [];
  }

  const { lowestValue, midpointValue, highestValue } = gradientColors;
  const [lowest, midpoint, highest] = range;

  if (midpointValue) {
    return [lowest, lowestValue, midpoint, midpointValue, highest, highestValue];
  }

  return [lowest, lowestValue, highest, highestValue];
};

const scaleStopPairs = (
  pairs,
  multiplier,
  { clampMin = 0, clampMax = 1 } = {},
) => pairs.flatMap(([stop, value]) => [
  stop,
  clamp(value * multiplier, clampMin, clampMax),
]);

const shiftRampPairs = (pairs, shift) => {
  const shiftedPairs = [];

  for (let index = 0; index < pairs.length; index += 2) {
    shiftedPairs.push(clamp(pairs[index] + shift, 0, 1), pairs[index + 1]);
  }

  return shiftedPairs;
};

const normalizeAscendingPairs = (pairs, { min = 0, max = 1, epsilon = 0.0001 } = {}) => {
  const entries = [];

  for (let index = 0; index < pairs.length; index += 2) {
    entries.push([pairs[index], pairs[index + 1]]);
  }

  entries.sort((left, right) => left[0] - right[0]);

  return entries.flatMap(([rawStop, value], index) => {
    const remaining = entries.length - index - 1;
    const previousStop = index === 0 ? min : entries[index - 1][0] + epsilon;
    const maxStop = max - (remaining * epsilon);
    const adjustedStop = clamp(rawStop, previousStop, maxStop);

    entries[index][0] = adjustedStop;

    return [adjustedStop, value];
  });
};

export const createHeatmapLayer = ({
  gradientColors,
  heatmapContrast = 50,
  heatmapVisibility = 50,
}) => {
  const contrastProfile = getContrastProfile(heatmapContrast);
  const visibilityProfile = getVisibilityProfile(heatmapVisibility);
  const densityColorStops = normalizeAscendingPairs([
    0, 'rgba(255,255,255,0)',
    clamp(0.08 + visibilityProfile.lowEndShift, 0.02, 0.18), 'rgba(255,255,255,0)',
    clamp(0.18 + visibilityProfile.lowEndShift, 0.06, 0.28), 'rgba(255,255,255,0)',
    ...shiftRampPairs(
      createHeatmapColorRamp(gradientColors),
      contrastProfile.rampShift - visibilityProfile.lowEndShift,
    ),
  ]);

  return {
    maxzoom: HEATMAP_MAX_ZOOM,
    type: 'heatmap',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'heatmap-weight': ['coalesce', ['get', HEATMAP_NORMALIZED_WEIGHT_PROPERTY], 0],
      'heatmap-intensity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        ...scaleStopPairs([
          [HEATMAP_MIN_ZOOM, 0.25],
          [HEATMAP_VISIBLE_START_ZOOM, 0.35],
          [HEATMAP_VISIBLE_FULL_ZOOM, 0.5],
          [10, 0.65],
          [14, 0.75],
          [HEATMAP_MAX_ZOOM, 0.85],
        ], contrastProfile.intensityMultiplier * visibilityProfile.intensityMultiplier),
      ],
      'heatmap-color': [
        'interpolate',
        ['linear'],
        ['heatmap-density'],
        ...densityColorStops,
      ],
      'heatmap-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        ...scaleStopPairs([
          [HEATMAP_MIN_ZOOM, 6],
          [HEATMAP_VISIBLE_START_ZOOM, 10],
          [HEATMAP_VISIBLE_FULL_ZOOM, 14],
          [10, 16],
          [14, 14],
          [HEATMAP_MAX_ZOOM, 12],
        ], contrastProfile.radiusMultiplier, { clampMin: 4, clampMax: 24 }),
      ],
      'heatmap-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        HEATMAP_VISIBLE_START_ZOOM, 0,
        ...scaleStopPairs([
          [HEATMAP_VISIBLE_FULL_ZOOM, 0.65],
          [10, 0.85],
          [HEATMAP_MAX_ZOOM, 0.95],
        ], contrastProfile.opacityMultiplier * visibilityProfile.opacityMultiplier),
      ],
    },
  };
};

export const heatmapLayer = (
  gradientColors,
  heatmapContrast = 50,
  heatmapVisibility = 50,
) => (
  createHeatmapLayer({
    gradientColors,
    heatmapContrast,
    heatmapVisibility,
  })
);
